import { Directive, ElementRef, Input, NgZone, OnChanges, OnDestroy, inject } from '@angular/core';

/**
 * Escreve no host um inteiro que sobe do valor exibido até `valor` por
 * `requestAnimationFrame`, com easing out — o contador de XP e de missões da
 * Órbita Financeira. Com `reduzirMovimento`, escreve o valor final no ato.
 *
 * O laço de quadros corre FORA da zone: a diretiva escreve `textContent`
 * direto, sem binding para detectar, e dentro da zone cada quadro seria uma
 * volta de change detection — e `ApplicationRef.isStable` ficava falso pelos
 * 700 ms inteiros (medido: `whenStable()` do overlay em ~800 ms).
 */
@Directive({ selector: '[mgContadorAnimado]' })
export class ContadorAnimadoDirective implements OnChanges, OnDestroy {
  @Input({ required: true, alias: 'mgContadorAnimado' }) valor = 0;
  @Input() duracaoMs = 700;
  @Input() reduzirMovimento = false;

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private exibido = 0;
  private quadro?: number;

  ngOnChanges(): void {
    this.cancelar();
    if (this.reduzirMovimento) {
      this.escrever(this.valor);
      return;
    }
    this.escrever(this.exibido);
    const de = this.exibido;
    const ate = this.valor;
    let inicio: number | null = null;
    const passo: FrameRequestCallback = agora => {
      inicio ??= agora;
      const t = Math.min(1, (agora - inicio) / Math.max(1, this.duracaoMs));
      const suavizado = 1 - Math.pow(1 - t, 3);
      this.escrever(Math.round(de + (ate - de) * suavizado));
      if (t < 1) {
        this.quadro = requestAnimationFrame(passo);
      }
    };
    this.zone.runOutsideAngular(() => {
      this.quadro = requestAnimationFrame(passo);
    });
  }

  ngOnDestroy(): void {
    this.cancelar();
  }

  private escrever(n: number): void {
    this.exibido = n;
    this.host.nativeElement.textContent = String(n);
  }

  private cancelar(): void {
    if (this.quadro !== undefined) {
      cancelAnimationFrame(this.quadro);
      this.quadro = undefined;
    }
  }
}
